export type Area = "orto" | "trauma" | "esporte" | "dor" | "gerontologia" | "combo";

export interface AreaConfig {
  slug: Area;
  route: string;
  posNome: string;
  posUrl: string;
  conversionIdentifier: string;
  /** Accent OKLCH color value — used as --primary scoped to the page. */
  accent: string;
  /** Solid hex used for button text (foreground over accent). */
  accentFg: string;
  /** Hex for radial gradient highlights in hero. */
  accentHex: string;
  numAulas: number;
  hero: {
    eyebrow: string;
    titulo: string;
    subtitulo: string;
    bullets: string[];
    receberTitulo?: string;
    receberEyebrow?: string;
    receberDescricao?: string;
  };
  faq: { q: string; a: string }[];
  metaTitle: string;
  metaDescription: string;
}

const baseFAQ = (
  numAulas: number,
  posLabel: "Especialização" | "Pós-Graduação" = "Pós-Graduação",
): { q: string; a: string }[] => [
  {
    q: "As aulas experimentais são gratuitas?",
    a: "Sim, 100% gratuitas. Você se inscreve, recebe o acesso por e-mail e assiste no seu tempo.",
  },
  {
    q: "Como recebo o acesso?",
    a: "Logo após preencher o formulário, você recebe um e-mail com o link de acesso às aulas.",
  },
  {
    q: "Quantas aulas eu recebo?",
    a: `${numAulas} aulas exclusivas da ${posLabel}.\u00a0`,
  },
  {
    q: "Estudantes de fisioterapia podem começar a pós?",
    a: `Não, apenas fisioterapeutas formados podem começar a ${posLabel}. Porém, as aulas experimentais também estão disponíveis para estudantes que desejam conhecer a metodologia da formação.\u00a0`,
  },
];

export const AREAS: Record<Area, AreaConfig> = {
  orto: {
    slug: "orto",
    route: "/aulas-experimentais-orto",
    posNome: "Pós-Graduação em Fisioterapia em Ortopedia e Trauma",
    posUrl: "https://pos-orto.fisioemortopedia.com.br/",
    conversionIdentifier: "aulas-exp-orto",
    accent: "oklch(0.78 0.16 175)", // teal/aqua, como o KV de Ortopedia
    accentFg: "#0a2a2a",
    accentHex: "#00C9A7",
    numAulas: 13,
    hero: {
      eyebrow: "13 AULAS GRATUITAS",
      titulo: "Conheça nossa Pós de Fisioterapia em Ortopedia\ne Traumatologia",
      subtitulo:
        "Para você saber como é estudar com alguns dos\u00a0maiores\nprofissionais do país, liberamos gratuitamente 13 aulas do\u00a0\nnosso\u00a0curso.",
      bullets: [
        "13 aulas exclusivas\u00a0",
        "Acesso gratuito e imediato",
        "Conteúdo 100% baseado em evidências",
      ],
      receberTitulo: "Uma amostra real da nossa pós\u00a0",
      receberEyebrow: "A SUA EXPERIÊNCIA COMEÇA AQUI",
      receberDescricao:
        "Experimente como é estudar com a gente e conheça a metodologia que já ajudou milhares de fisioterapeutas a evoluírem sua prática clínica.",
    },
    faq: baseFAQ(13),
    metaTitle: "13 Aulas Grátis, Pós em Ortopedia | FEO",
    metaDescription:
      "Receba gratuitamente 13 aulas da Pós-Graduação em Fisioterapia em Ortopedia da FEO, uma aula de cada módulo do curso, direto no seu e-mail.",
  },
  trauma: {
    slug: "trauma",
    route: "/aulas-experimentais-trauma",
    posNome: "Pós-Graduação em Fisioterapia em Traumatologia",
    posUrl: "https://pos-traumato.fisioemortopedia.com.br/",
    conversionIdentifier: "aulas-exp-trauma",
    accent: "oklch(0.76 0.07 82)", // dourado/mostarda suave da thumb de Trauma
    accentFg: "#2e2712",
    accentHex: "#C6B077",
    numAulas: 6,
    hero: {
      eyebrow: "6 AULAS GRATUITAS",
      titulo: "Conheça nossa Pós\nde Fisioterapia em Traumatologia",
      subtitulo:
        "Para você saber como é estudar com alguns dos\u00a0maiores\nprofissionais do país, liberamos gratuitamente 6 aulas do\u00a0\nnosso\u00a0curso.",
      bullets: [
        "6 aulas exclusivas\u00a0",
        "Acesso gratuito e imediato",
        "Conteúdo 100% baseado em evidências",
      ],
      receberTitulo: "Uma amostra real da nossa pós\u00a0",
      receberEyebrow: "A SUA EXPERIÊNCIA COMEÇA AQUI",
      receberDescricao:
        "Experimente como é estudar com a gente e conheça a metodologia que já ajudou milhares de fisioterapeutas a evoluírem sua prática clínica.",
    },
    faq: baseFAQ(6),
    metaTitle: "6 Aulas Grátis, Pós em Traumatologia | FEO",
    metaDescription:
      "Conheça a 1ª pós online do Brasil 100% focada em Traumatologia. Receba 6 aulas especiais gratuitas da Pós FEO direto no seu e-mail.",
  },
  esporte: {
    slug: "esporte",
    route: "/aulas-experimentais-esporte",
    posNome: "Pós-Graduação em Fisioterapia em Esporte e Atividade Física",
    posUrl: "https://www.pos-esporte.fisioemortopedia.com.br/",
    conversionIdentifier: "aulas-exp-esporte",
    accent: "oklch(0.66 0.20 22)", // vermelho/crimson do KV de Esporte
    accentFg: "#2a0a0a",
    accentHex: "#D63A48",
    numAulas: 10,
    hero: {
      eyebrow: "10 AULAS GRATUITAS",
      titulo:
        "Conheça nossa Pós de Fisioterapia em Esporte\ne Atividade Física",
      subtitulo:
        "Para você saber como é estudar com alguns dos\u00a0maiores\nprofissionais do país, liberamos gratuitamente 10 aulas do\u00a0\nnosso\u00a0curso.",
      bullets: [
        "10 aulas exclusivas\u00a0",
        "Acesso gratuito e imediato",
        "Conteúdo 100% baseado em evidências",
      ],
      receberTitulo: "Uma amostra real da nossa pós\u00a0",
      receberEyebrow: "A SUA EXPERIÊNCIA COMEÇA AQUI",
      receberDescricao:
        "Experimente como é estudar com a gente e conheça a metodologia que já ajudou milhares de fisioterapeutas a evoluírem sua prática clínica.",
    },
    faq: baseFAQ(10),
    metaTitle: "10 Aulas Grátis, Pós em Esporte | FEO",
    metaDescription:
      "Receba gratuitamente 10 aulas da Pós-Graduação em Fisioterapia em Esporte e Atividade Física da FEO, direto no seu e-mail, no seu ritmo.",
  },
  dor: {
    slug: "dor",
    route: "/aulas-experimentais-dor",
    posNome: "Especialização em Dor para Fisioterapeutas",
    posUrl: "https://www.pos-dor.fisioemortopedia.com.br/",
    conversionIdentifier: "aulas-exp-dor",
    accent: "oklch(0.72 0.15 275)", // azul-periwinkle do KV de Dor
    accentFg: "#0f0f3a",
    accentHex: "#7C7FFF",
    numAulas: 8,
    hero: {
      eyebrow: "8 AULAS GRATUITAS",
      titulo: "Conheça nossa Especialização em Dor\npara Fisioterapeutas",
      subtitulo:
        "Para você saber como é estudar com alguns dos\u00a0maiores\nprofissionais do país, liberamos gratuitamente 8 aulas do\u00a0\nnosso\u00a0curso.",
      bullets: [
        "8 aulas exclusivas\u00a0",
        "Acesso gratuito e imediato",
        "Conteúdo 100% baseado em evidências",
      ],
      receberTitulo: "Uma amostra real da nossa pós\u00a0",
      receberEyebrow: "A SUA EXPERIÊNCIA COMEÇA AQUI",
      receberDescricao:
        "Experimente como é estudar com a gente e conheça a metodologia que já ajudou milhares de fisioterapeutas a evoluírem sua prática clínica.",
    },
    faq: baseFAQ(8, "Especialização"),
    metaTitle: "8 Aulas Grátis, Especialização em Dor | FEO",
    metaDescription:
      "Receba gratuitamente 8 aulas da Especialização em Dor para Fisioterapeutas da FEO, uma amostra real do curso, direto no seu e-mail.",
  },
  gerontologia: {
    slug: "gerontologia",
    route: "/aulas-experimentais-gerontologia",
    posNome: "Pós-Graduação em Fisioterapia em Gerontologia",
    posUrl: "https://pos-geronto.fisioemortopedia.com.br/",
    conversionIdentifier: "aulas-exp-gerontologia",
    accent: "oklch(0.68 0.20 305)", // roxo/violeta do KV de Gerontologia
    accentFg: "#220a3a",
    accentHex: "#9B5BFE",
    numAulas: 6,
    hero: {
      eyebrow: "6 AULAS GRATUITAS",
      titulo: "Conheça nossa Pós\nde Fisioterapia em Gerontologia\n",
      subtitulo:
        "Para você saber como é estudar com alguns dos\u00a0maiores\nprofissionais do país, liberamos gratuitamente 6 aulas do\u00a0\nnosso\u00a0curso.",
      bullets: [
        "6 aulas exclusivas\u00a0",
        "Acesso gratuito e imediato",
        "Conteúdo 100% baseado em evidências",
      ],
      receberTitulo: "Uma amostra real da nossa pós\u00a0",
      receberEyebrow: "A SUA EXPERIÊNCIA COMEÇA AQUI",
      receberDescricao:
        "Experimente como é estudar com a gente e conheça a metodologia que já ajudou milhares de fisioterapeutas a evoluírem sua prática clínica.",
    },
    faq: baseFAQ(6),
    metaTitle: "6 Aulas Grátis, Pós em Gerontologia | FEO",
    metaDescription:
      "Receba gratuitamente 6 aulas da Pós-Graduação em Fisioterapia em Gerontologia da FEO, direto no seu e-mail, no seu ritmo.",
  },
  combo: {
    slug: "combo",
    route: "/aulas-experimentais-pos-todas",
    posNome: "Combo das Pós-Graduações Fisio em Ortopedia (FEO)",
    posUrl: "https://fisioemortopedia.com.br/",
    conversionIdentifier: "aulas-exp-combo",
    accent: "oklch(0.74 0.16 200)", // ciano-azulado FEO
    accentFg: "#062033",
    accentHex: "#2BD4D9",
    numAulas: 44,
    hero: {
      eyebrow: "44 Aulas Experimentais Gratuitas",
      titulo: "Conheça por dentro todas as pós-graduações da FEO",
      subtitulo:
        "Uma seleção das melhores aulas das nossas 5 pós-graduações reunidas em um só lugar. Veja como é estudar com a gente e descubra qual área mais combina com você.",
      bullets: [
        "44 aulas selecionadas das nossas 5 pós-graduações",
        "Conteúdo direto dos professores que ministram os cursos",
        "Acesso 100% gratuito e imediato",
      ],
      receberTitulo: "Uma amostra real da nossa pós\u00a0",
      receberEyebrow: "A SUA EXPERIÊNCIA COMEÇA AQUI",
      receberDescricao:
        "Experimente como é estudar com a gente e conheça a metodologia que já ajudou milhares de fisioterapeutas a evoluírem sua prática clínica.",
    },
    faq: [
      {
        q: "As aulas experimentais são gratuitas?",
        a: "Sim, 100% gratuitas. Você se inscreve, recebe o acesso por e-mail e assiste no seu tempo.",
      },
      {
        q: "Como recebo o acesso?",
        a: "Logo após preencher o formulário, você recebe um e-mail com o link de acesso às aulas de todas as pós.",
      },
      {
        q: "Quantas aulas eu recebo?",
        a: "44 aulas no total, uma seleção representativa das nossas 5 pós-graduações (Ortopedia, Traumatologia, Esporte, Dor e Gerontologia).",
      },
      {
        q: "Preciso escolher uma pós para receber as aulas?",
        a: "Não. Aqui você assiste aulas de todas as áreas, no seu ritmo, e descobre qual pós faz mais sentido para a sua carreira.",
      },
      {
        q: "Preciso ter experiência prévia?",
        a: "Não. As aulas são abertas a fisioterapeutas e estudantes em diferentes níveis de experiência.",
      },
    ],
    metaTitle: "44 Aulas Grátis, Todas as Pós da FEO",
    metaDescription:
      "Conheça por dentro todas as pós-graduações da Fisio em Ortopedia. 44 aulas gratuitas selecionadas das nossas 5 pós, direto no seu e-mail.",
  },
};
